import { getRateXcdTo, getXcdToUsd } from '@/stores/fxStore';

/** Currencies shoppers can use for display (listings are stored in XCD or USD). */
export type DisplayCurrency = 'XCD' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'BBD' | 'TTD' | 'JMD';

export const DISPLAY_CURRENCIES: DisplayCurrency[] = [
  'XCD',
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'BBD',
  'TTD',
  'JMD',
];

export const CURRENCY_INFO: Record<
  DisplayCurrency,
  { flag: string; label: string; name: string }
> = {
  XCD: { flag: '🇬🇩', label: 'XCD', name: 'Eastern Caribbean dollar' },
  USD: { flag: '🇺🇸', label: 'USD', name: 'US dollar' },
  EUR: { flag: '🇪🇺', label: 'EUR', name: 'Euro' },
  GBP: { flag: '🇬🇧', label: 'GBP', name: 'British pound' },
  CAD: { flag: '🇨🇦', label: 'CAD', name: 'Canadian dollar' },
  BBD: { flag: '🇧🇧', label: 'BBD', name: 'Barbados dollar' },
  TTD: { flag: '🇹🇹', label: 'TTD', name: 'Trinidad & Tobago dollar' },
  JMD: { flag: '🇯🇲', label: 'JMD', name: 'Jamaican dollar' },
};

export function isDisplayCurrency(value: string): value is DisplayCurrency {
  return (DISPLAY_CURRENCIES as string[]).includes(value);
}

/** Normalize listing currency (XCD or USD) to XCD cents. */
function toXcdCents(cents: number, fromCurrency: string): number {
  const from = fromCurrency === 'USD' ? 'USD' : 'XCD';
  if (from === 'XCD') return cents;
  const xcdToUsd = getXcdToUsd();
  if (xcdToUsd <= 0) return cents;
  return Math.round(cents / xcdToUsd);
}

export function convertCents(
  cents: number,
  fromCurrency: string,
  toCurrency: DisplayCurrency,
): number {
  const xcdCents = toXcdCents(cents, fromCurrency);
  if (toCurrency === 'XCD') return xcdCents;
  const rate = getRateXcdTo(toCurrency);
  return Math.round(xcdCents * rate);
}

export function formatMoney(cents: number, currency: DisplayCurrency): string {
  const amount = (cents / 100).toLocaleString('en-GD', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency === 'EUR') return `EUR €${amount}`;
  if (currency === 'GBP') return `GBP £${amount}`;
  return `${currency} $${amount}`;
}

/** XCD dollar amount (not cents) → same amount expressed in display currency dollars. */
export function xcdDollarsToDisplayAmount(xcdDollars: number, to: DisplayCurrency): number {
  if (to === 'XCD') return xcdDollars;
  return xcdDollars * getRateXcdTo(to);
}

/** Display-currency dollar amount → XCD dollars (for filters stored canonically in XCD). */
export function displayAmountToXcdDollars(amount: number, from: DisplayCurrency): number {
  if (from === 'XCD') return amount;
  const rate = getRateXcdTo(from);
  if (rate <= 0) return amount;
  return amount / rate;
}
